/** M8-26 品牌使用授权（所有者 → 授权商家）：查询 / 申请 / 同页审核。
 *
 * 契约：`docs/Dist/市场主体关系方案.md` §3.4。
 * 三块在同一页按身份显示：
 *   - 我的品牌（`brand.js`，不变）
 *   - 申请品牌授权（全部商家）：查平台现有品牌 → 提交申请 → 看自己申请的进度
 *   - 授权申请审核（仅品牌所有者）：待审 / 已通过 / 已驳回；批准、驳回、撤回
 *
 * 这里只做「取数 + 挂事件」；渲染在 `brand-auth-view.js`（无 DOM，可单测）。
 * 能不能按由后端 `can_review / can_withdraw / can_disable` 说话 —— 那三个字段就是
 * 后端身份判定的结论，前端再判一遍只会与后端判得不一样。
 */
import { $ } from "../lib/dom.js";
import { api } from "../api.js";
import { showBanner } from "./index.js";
import {
  renderInbox,
  renderMyApplications,
  renderMyClaims,
  renderSearchRows,
  splitList,
} from "./brand-auth-view.js";

/** 品牌写权限：与后端 `mc:brand:write` 闸同一码（M8-26-12 不新造平台权限码）。 */
export function canWriteBrand() {
  if (!window.McAuth || !window.McAuth.hasPerm) return true;
  return window.McAuth.hasPerm("mc:brand:write");
}

// ── 状态 ──────────────────────────────────────────────

let authCache = { applications: [], inbox: [], claims: [] };
let pendingApply = null; // { brand_id, brand_name }
let pendingClaim = null; // { brand_id, brand_name }
let pendingReview = null; // { authorization_id, mode }

function setMsg(id, text, ok) {
  const el = $(id);
  if (!el) return;
  el.textContent = text || "";
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
  el.classList.toggle("err", !!text && !ok);
}

function hideApplyForm() {
  pendingApply = null;
  const form = $("brand-apply-form");
  if (form) form.classList.add("hidden");
}

function hideReviewForm() {
  pendingReview = null;
  const form = $("brand-review-form");
  if (form) form.classList.add("hidden");
}

async function loadAuthLists() {
  const box = $("brand-my-applications");
  const claimBox = $("brand-my-claims");
  if (box) box.innerHTML = "<p class='mc-desc muted'>加载中…</p>";
  if (claimBox) claimBox.innerHTML = "<p class='mc-desc muted'>加载中…</p>";
  const [{ ok, data }, claimRes] = await Promise.all([
    api("GET", "/api/v1/fitting/mall/brand/owner-authorizations"),
    api("GET", "/api/v1/fitting/mall/brand/owner-claims"),
  ]);
  if (!ok) {
    if (box) box.innerHTML = "<p class='mc-desc muted'>授权申请加载失败。</p>";
    if (claimBox) claimBox.innerHTML = "<p class='mc-desc muted'>认领记录加载失败。</p>";
    return;
  }
  authCache = {
    applications: (data && data.applications) || [],
    inbox: (data && data.inbox) || [],
    claims: (claimRes.ok && claimRes.data && claimRes.data.items) || [],
  };
  if (box) box.innerHTML = renderMyApplications(authCache.applications);
  bindRowActions("brand-my-applications");
  if (claimBox) claimBox.innerHTML = renderMyClaims(authCache.claims);
  bindRowActions("brand-my-claims");
  const reviewHost = $("brand-review-host");
  if (reviewHost) reviewHost.innerHTML = renderInbox(authCache.inbox);
  bindRowActions("brand-review-host");
}

/** 行内按钮统一在这里挂事件（每次重渲染后重挂，避免内联 onclick）。 */
function bindRowActions(hostId) {
  const host = $(hostId);
  if (!host) return;
  host.querySelectorAll(".btn-brand-apply").forEach((btn) => {
    btn.addEventListener("click", () => {
      openApplyForm(btn.getAttribute("data-brand-id"), btn.getAttribute("data-brand-name"));
    });
  });
  host.querySelectorAll(".btn-brand-withdraw").forEach((btn) => {
    btn.addEventListener("click", () => withdraw(btn.getAttribute("data-auth-id")));
  });
  host.querySelectorAll(".btn-brand-claim").forEach((btn) => {
    btn.addEventListener("click", () =>
      openClaimForm(btn.getAttribute("data-brand-id"), btn.getAttribute("data-brand-name"))
    );
  });
  host.querySelectorAll(".btn-brand-claim-withdraw").forEach((btn) => {
    btn.addEventListener("click", () => withdrawClaim(btn.getAttribute("data-claim-id")));
  });
  host.querySelectorAll(".btn-brand-approve").forEach((btn) => {
    btn.addEventListener("click", () =>
      openReviewForm(btn.getAttribute("data-auth-id"), "approve")
    );
  });
  host.querySelectorAll(".btn-brand-reject").forEach((btn) => {
    btn.addEventListener("click", () =>
      openReviewForm(btn.getAttribute("data-auth-id"), "reject")
    );
  });
  host.querySelectorAll(".btn-brand-disable").forEach((btn) => {
    btn.addEventListener("click", () => disable(btn.getAttribute("data-auth-id")));
  });
}

function openApplyForm(brandId, brandName) {
  if (!canWriteBrand()) {
    showBanner("当前账号无品牌写权限（mc:brand:write），无法申请", false);
    return;
  }
  pendingApply = { brand_id: brandId, brand_name: brandName };
  const form = $("brand-apply-form");
  const target = $("brand-apply-target");
  if (target) target.textContent = `申请品牌：${brandName}（${brandId}）`;
  const note = $("brand-apply-note");
  if (note) note.value = "";
  setMsg("brand-apply-msg", "", true);
  if (form) form.classList.remove("hidden");
  if (note) note.focus();
}

function hideClaimForm() {
  pendingClaim = null;
  const form = $("brand-claim-form");
  if (form) form.classList.add("hidden");
}

function openClaimForm(brandId, brandName) {
  if (!canWriteBrand()) {
    showBanner("当前账号无品牌写权限（mc:brand:write），无法提交认领", false);
    return;
  }
  pendingClaim = { brand_id: brandId, brand_name: brandName };
  const form = $("brand-claim-form");
  const target = $("brand-claim-target");
  if (target) target.textContent = `认领品牌：${brandName}（${brandId}）`;
  const note = $("brand-claim-note");
  if (note) note.value = "";
  const evidence = $("brand-claim-evidence");
  if (evidence) evidence.value = "";
  setMsg("brand-claim-msg", "", true);
  if (form) form.classList.remove("hidden");
  if (note) note.focus();
}

async function submitClaim() {
  if (!pendingClaim) return;
  const note = ($("brand-claim-note") && $("brand-claim-note").value.trim()) || "";
  const evidence = ($("brand-claim-evidence") && $("brand-claim-evidence").value.trim()) || "";
  if (!note && !evidence) {
    setMsg("brand-claim-msg", "请写清权利来源，或给出材料链接（平台要据此核验）", false);
    return;
  }
  const btn = $("btn-brand-claim-submit");
  if (btn) btn.disabled = true;
  const { ok, data, status } = await api("POST", "/api/v1/fitting/mall/brand/owner-claims", {
    body: {
      brand_id: pendingClaim.brand_id,
      claim_note: note || null,
      evidence_url: evidence || null,
    },
  });
  if (btn) btn.disabled = false;
  if (ok) {
    hideClaimForm();
    setMsg("brand-search-status", "认领已提交，等平台核验", true);
    await Promise.all([loadAuthLists(), searchBrands()]);
  } else {
    setMsg("brand-claim-msg", formError(data, status, "提交失败"), false);
  }
}

async function withdrawClaim(claimId) {
  const { ok, data, status } = await api(
    "POST",
    `/api/v1/fitting/mall/brand/owner-claims/${claimId}/withdraw`
  );
  if (!ok) {
    showBanner(formError(data, status, "撤回失败"), false);
    return;
  }
  await Promise.all([loadAuthLists(), searchBrands()]);
}

function openReviewForm(authorizationId, mode) {
  if (!canWriteBrand()) {
    showBanner("当前账号无品牌写权限（mc:brand:write），无法审核", false);
    return;
  }
  const rec = (authCache.inbox || []).find((r) => r.authorization_id === authorizationId);
  pendingReview = { authorization_id: authorizationId, mode };
  const form = $("brand-review-form");
  const target = $("brand-review-target");
  const scopeWrap = $("brand-review-scope");
  const rejectWrap = $("brand-review-reject-wrap");
  const who = (rec && rec.grantee_merchant_name) || authorizationId;
  const brand = (rec && rec.brand_name) || "";
  if (target) {
    target.textContent =
      mode === "approve" ? `批准：${who} 使用「${brand}」` : `驳回：${who} 的申请（${brand}）`;
  }
  if (scopeWrap) scopeWrap.classList.toggle("hidden", mode !== "approve");
  if (rejectWrap) rejectWrap.classList.toggle("hidden", mode !== "reject");
  const req = $("brand-review-reason");
  if (req) req.value = "";
  setMsg("brand-review-msg", "", true);
  if (form) form.classList.remove("hidden");
  if (form) {
    try {
      form.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* ignore */
    }
  }
}

function formError(data, status, fallback) {
  return (
    (data && data.error && data.error.message) ||
    (data && data.message) ||
    (status === 0 ? "网络错误或接口不可达" : fallback)
  );
}

async function submitApply() {
  if (!pendingApply) return;
  const note = ($("brand-apply-note") && $("brand-apply-note").value.trim()) || "";
  const btn = $("btn-brand-apply-submit");
  if (btn) btn.disabled = true;
  const { ok, data, status } = await api(
    "POST",
    "/api/v1/fitting/mall/brand/owner-authorizations",
    { body: { brand_id: pendingApply.brand_id, apply_note: note || null } }
  );
  if (btn) btn.disabled = false;
  if (ok) {
    hideApplyForm();
    setMsg("brand-search-status", "申请已提交，等待品牌所有者审核", true);
    await Promise.all([loadAuthLists(), searchBrands()]);
  } else {
    setMsg("brand-apply-msg", formError(data, status, "提交失败"), false);
  }
}

function reviewBody() {
  const body = {};
  if (pendingReview && pendingReview.mode === "approve") {
    body.scope = {
      channels: splitList($("brand-review-channels") && $("brand-review-channels").value),
      regions: splitList($("brand-review-regions") && $("brand-review-regions").value),
      exclusive: !!($("brand-review-exclusive") && $("brand-review-exclusive").checked),
    };
    const from = ($("brand-review-from") && $("brand-review-from").value) || "";
    const to = ($("brand-review-to") && $("brand-review-to").value) || "";
    if (from) body.effective_from = from;
    if (to) body.effective_to = to;
  } else {
    body.reject_reason =
      ($("brand-review-reason") && $("brand-review-reason").value.trim()) || "";
  }
  return body;
}

async function submitReview() {
  if (!pendingReview) return;
  const body = reviewBody();
  if (pendingReview.mode === "reject" && !body.reject_reason) {
    setMsg("brand-review-msg", "请填写驳回原因（发起方要看得见）", false);
    return;
  }
  const url = `/api/v1/fitting/mall/brand/owner-authorizations/${
    pendingReview.authorization_id
  }/${pendingReview.mode === "approve" ? "approve" : "reject"}`;
  const btn = $("btn-brand-review-submit");
  if (btn) btn.disabled = true;
  const { ok, data, status } = await api("POST", url, { body });
  if (btn) btn.disabled = false;
  if (ok) {
    const done = pendingReview.mode === "approve" ? "已批准，授权即时生效" : "已驳回";
    hideReviewForm();
    await loadAuthLists();
    showBanner(done, true);
  } else {
    setMsg("brand-review-msg", formError(data, status, "处理失败"), false);
  }
}

async function withdraw(authorizationId) {
  const { ok, data, status } = await api(
    "POST",
    `/api/v1/fitting/mall/brand/owner-authorizations/${authorizationId}/withdraw`
  );
  if (!ok) {
    showBanner(formError(data, status, "撤回失败"), false);
    return;
  }
  await Promise.all([loadAuthLists(), searchBrands()]);
}

async function disable(authorizationId) {
  const { ok, data, status } = await api(
    "POST",
    `/api/v1/fitting/mall/brand/owner-authorizations/${authorizationId}/disable`
  );
  if (!ok) {
    showBanner(formError(data, status, "停用失败"), false);
    return;
  }
  await loadAuthLists();
}

async function searchBrands() {
  const keyword = ($("brand-search-keyword") && $("brand-search-keyword").value.trim()) || "";
  const status = $("brand-search-status");
  const host = $("brand-search-host");
  if (status) status.textContent = "查询中…";
  if (host) host.innerHTML = "";
  const { ok, data } = await api(
    "GET",
    `/api/v1/fitting/mall/brands/platform-search?keyword=${encodeURIComponent(keyword)}`
  );
  if (!ok) {
    if (status) status.textContent = "品牌检索失败";
    return;
  }
  const items = (data && data.items) || [];
  if (status) {
    status.textContent = items.length
      ? `共 ${items.length} 个平台品牌`
      : keyword
      ? `没有匹配「${keyword}」的平台品牌`
      : "平台暂无已通过审核的品牌";
  }
  if (host) {
    host.innerHTML = items.length ? renderSearchRows(items) : "";
    bindRowActions("brand-search-host");
  }
}

/** 品牌面板打开时调用（`brand.js` 的 `loadBrandPanel` 串上来）。 */
export async function loadBrandAuthSection() {
  hideApplyForm();
  hideReviewForm();
  hideClaimForm();
  await Promise.all([loadAuthLists(), searchBrands()]);
}

export function initBrandAuthHandlers() {
  const search = $("btn-brand-search");
  if (search) search.addEventListener("click", () => searchBrands());
  const kw = $("brand-search-keyword");
  if (kw) {
    kw.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        searchBrands();
      }
    });
  }
  const submitApplyBtn = $("btn-brand-apply-submit");
  if (submitApplyBtn) submitApplyBtn.addEventListener("click", () => submitApply());
  const submitClaimBtn = $("btn-brand-claim-submit");
  if (submitClaimBtn) submitClaimBtn.addEventListener("click", () => submitClaim());
  const cancelClaim = $("btn-brand-claim-cancel");
  if (cancelClaim) cancelClaim.addEventListener("click", () => hideClaimForm());
  const cancelApply = $("btn-brand-apply-cancel");
  if (cancelApply) cancelApply.addEventListener("click", () => hideApplyForm());
  const submitReviewBtn = $("btn-brand-review-submit");
  if (submitReviewBtn) submitReviewBtn.addEventListener("click", () => submitReview());
  const cancelReview = $("btn-brand-review-cancel");
  if (cancelReview) cancelReview.addEventListener("click", () => hideReviewForm());
}
